import React from 'react';
import { useEffect, useState, useRef } from 'react';
import Layout from '../../Layout/Layout';
import { Container, Row, Col, Button } from 'reactstrap';
import TitleBar from '../../Layout/TitleBar';
import {
  useTranslator,
  useToast,
  useAppContext,
  withLoading,
  withMobileAppMode,
  cloudflareImage,
  callNativeMobileApp,
  getCountry,
} from '@front-activerse/common-components';
import CookbookDish from './CookbookDish';
import IngredientList from './CookbookIngredientList';
import Ingredient from './CookbookIngredient';
import CookbookNutritions from './CookbookNutritions';
import UniversalPlaceholder from '../../Layout/UniversalPlaceholder';
import { compareVersions } from 'compare-versions';
import { useOutletContext } from 'react-router-dom';

interface ICookbookMealDetailsContainer {
  meal: any;
  addToFavorites: (mealId: number) => Promise<any>;
  removeFromFavorites: (mealId: number) => Promise<any>;
  mobileApp: any;
}

export const CookbookMealDetailsContainer: React.FC<ICookbookMealDetailsContainer> = ({
  meal,
  addToFavorites,
  removeFromFavorites,
  mobileApp,
}) => {
  const { t } = useTranslator();
  const { toastError, toastSuccess, dismiss } = useToast();
  const [visiblePoster, setVisiblePoster] = useState(true);
  const [multiplier, setMultiplier] = useState(1);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [preloadInstacart, setPreloadInstacart] = useState(false);

  const {
    consts: { INSTACART_FIREBASE_FUNCTION, INSTACART_URL, APP_BRAND },
    images: {
      favouriteFull,
      favouriteEmpty,
      playBtn,
      instacartLogo,
      plus,
      plusMulti,
      minus,
      minusMulti,
      star,
    },
  } = useAppContext();

  const { trackFirebaseEvent } = useOutletContext<{
    trackFirebaseEvent: (eventName: string, eventPayload: object) => void;
  }>();

  useEffect(() => {
    trackFirebaseEvent('screen_view', {
      firebase_screen: 'Meal_details',
      meal_id: meal.id,
      cookbook_meal_types: meal.tags.mealTypeTags.map((tag: any) => tag.name).join('/'),
      meals_tab: 'cookbook',
      meal_favourites: meal.isFavorite,
      selected_diet: meal.tags.dietTypeTags.map((tag: any) => tag.name).join('/'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeMultiplier = (multiplier: number) => {
    setMultiplier(multiplier);
  };

  const renderMealVideo = () => {
    if (!meal.preparationVideoUrl) {
      return renderMealImage();
    }

    return (
      <div className="embed-responsive ratio ratio-1x1">
        <video
          ref={videoRef}
          src={meal.preparationVideoUrl}
          controls
          playsInline
          controlsList="nodownload"
          disablePictureInPicture
          onEnded={() => setVisiblePoster(true)}
        />
        {visiblePoster ? (
          <div
            style={{
              backgroundImage: `url(${meal.preparationImageUrl
                .replace('(', '%28')
                .replace(')', '%29')})`,
            }}
            className="embed-responsive-item d-flex align-items-center justify-content-center"
            onClick={() => playVideo(videoRef)}
          >
            <img src={playBtn} alt="" />
          </div>
        ) : (
          ''
        )}
      </div>
    );
  };

  const renderMealImage = () => {
    if (meal.preparationImageUrl) {
      const coverImage = cloudflareImage(
        meal.preparationImageUrl,
        `${APP_BRAND}`,
        'cookbookMealDetails'
      );
      return <img src={coverImage} className="img-fluid" width={768} height={768} alt="" />;
    }
    return null;
  };

  const playVideo = async (myVideoRef: any) => {
    setVisiblePoster(false);

    try {
      await myVideoRef.current.play();
    } catch (err) {
      toastError();
      console.warn(err);
    }
  };

  const toggleFavorite = async () => {
    try {
      if (meal.isFavorite) {
        const response = await removeFromFavorites(meal.id);
        if (response.data.cookbook.removeFavoriteMeal.code === 200) {
          toastSuccess(
            <p className="mb-0">
              {t('cookbook/fav-meals/removed')}
              <Button
                color="link"
                onClick={() => {
                  addToFavorites(meal.id);
                  dismiss();
                }}
              >
                {t('cookbook/fav-meals/back')}
              </Button>
            </p>
          );
        }
      } else {
        const response = await addToFavorites(meal.id);
        if (response.data.cookbook.addFavoriteMeal.successCode === 200) {
          toastSuccess(<p className="mb-0">{t('cookbook/fav-meals/added')}</p>);
        }
      }

      trackFirebaseEvent('user_action', {
        action: 'tap-add_favourites',
        location: 'Dietplan',
        meal_id: meal.id,
        cookbook_meal_types: meal.tags.mealTypeTags
          ? meal.tags.mealTypeTags.map((tag: any) => tag.name).join('/')
          : '',
        selected_diet: meal.tags.dietTypeTags
          ? meal.tags.dietTypeTags.map((tag: any) => tag.name).join('/')
          : '',
        meals_tab: 'cookbook',
        meal_favourites: meal.isFavorite,
      });
    } catch (err) {
      toastError();
    }
  };

  const handleInstaCart = async () => {
    const ingredients: any = [];

    meal.dishes.forEach((dish: any) => {
      dish.ingredients.forEach((ingredient: any) => {
        const measurement = ingredient.measurements[0];
        const measure = `${measurement.amount} ${measurement.text.replace('%s', '')}`;

        ingredients.push(`${measure} ${ingredient.name}`);
      });
    });

    let mealImg = '';

    if (meal.preparationImageUrl) {
      mealImg = meal.preparationImageUrl;
    }

    const request = {
      name: meal.name,
      ingredients,
      recipe: [],
      image: mealImg,
      description: `Cookbook recipe`,
    };

    setPreloadInstacart(true);

    const response = await fetch(INSTACART_FIREBASE_FUNCTION.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    response.json().then((data) => {
      setPreloadInstacart(false);

      if (data.instacartUrl) {
        const recipeData = encodeURIComponent(data.instacartUrl);
        const instacartUrl = `${INSTACART_URL}?recipeSourceUrl=${recipeData}&utm_source=instacart_growth_partnerships&aff_id=1818&offer_id=1&affiliate_platform=recipe_widget`;

        trackFirebaseEvent('user_action', {
          action: 'tap-instacart_button',
          location: 'Meal_details',
          meals_tab: 'cookbook',
        });

        if (
          mobileApp &&
          mobileApp.os &&
          mobileApp.os.toLowerCase().startsWith('ios') &&
          mobileApp.version &&
          compareVersions(mobileApp.version, '4.0.2')
        ) {
          callNativeMobileApp({
            action: 'deeplink_open',
            payload: {
              url: instacartUrl,
            },
          });
        } else {
          // workound for typescript error
          const win: Window = window;
          win.location = instacartUrl;
        }
      }
    });
  };

  const renderIngredients = (ingredients: any, dishId: number) =>
    ingredients.map((ingredient: any) => {
      return (
        <Ingredient
          key={ingredient.key}
          name={ingredient.name}
          measurements={ingredient.measurements}
          multiplier={multiplier}
          isReplaceable={ingredient.isReplaceable}
          replacements={ingredient.replacements}
          isReplaced={ingredient.isReplaced}
          originalIngredient={ingredient.originalIngredient}
          dishId={dishId}
          id={ingredient.id}
          mealId={meal.id}
          ingredientKey={ingredient.key}
        />
      );
    });

  const renderIngredientList = (ingredients: any, dishId: number) => {
    const filteredIngredients = ingredients.filter(
      (ingredient: any) => !ingredient.category.isSeasoning && !ingredient.category.isOptional
    );

    const onlySeasoningsIngredients = ingredients.filter(
      (ingredient: any) => ingredient.category.isSeasoning
    );

    const optionalIngredients = ingredients.filter(
      (ingredient: any) => ingredient.category.isOptional
    );

    return (
      <IngredientList
        renderIngredients={() => renderIngredients(filteredIngredients, dishId)}
        renderOnlySeasoningsIngredients={() => renderIngredients(onlySeasoningsIngredients, dishId)}
        renderOptionalIngredients={() => renderIngredients(optionalIngredients, dishId)}
      />
    );
  };

  const renderTags = () => {
    return (
      <div className="tags">
        {meal.tags.customTags &&
          meal.tags.customTags.map((tag: { id: number; labelColor: string; name: string }) => (
            <span
              className="tag me-1"
              key={tag.id}
              style={{ backgroundColor: tag.labelColor, color: '#fff' }}
            >
              {tag.name}
            </span>
          ))}
        {meal.tags.mealTypeTags &&
          meal.tags.mealTypeTags.map((tag: { id: number; name: string }) => (
            <span className="tag me-1" key={tag.id}>
              {tag.name}
            </span>
          ))}
        {meal.tags.dietTypeTags &&
          meal.tags.dietTypeTags.map((tag: { id: number; name: string }) => (
            <span className="tag me-1" key={tag.id}>
              {tag.name}
            </span>
          ))}
      </div>
    );
  };

  let showInstacartButton = false;
  if (getCountry() === 'Canada' || getCountry() === 'United States of America') {
    showInstacartButton = true;
  }

  const calculatePreparationTime = (minutes: number) => {
    const steps = [5, 15, 30, 45, 60];

    if (minutes === 0 || minutes == null) {
      return null;
    }

    for (const step of steps) {
      if (minutes <= step) {
        return `< ${step}`;
      }
    }
    return `> ${steps[steps.length - 1]}`;
  };

  return (
    <Layout>
      <TitleBar level={3}>{t(`cookbook/meal-details`)}</TitleBar>
      <div className="page-template-cookbook">
        <Container className="py-md-4">
          <Row>
            <Col md={{ size: 10, offset: 1 }}>
              <div className="page-container">
                <div className="meal-details">
                  <Row>
                    <Col md="6">
                      <div className="meal-image-video">
                        {renderMealVideo()}
                        <Button className="add-remove-fav" color="link" onClick={toggleFavorite}>
                          <img
                            src={meal.isFavorite ? favouriteFull : favouriteEmpty}
                            alt=""
                            width="32"
                            height="32"
                          />
                        </Button>
                        {visiblePoster && (
                          <div className="meal-info2 d-md-none">
                            <Container>
                              {renderTags()}
                              <h1 className="meal-title title-large semi-bold white mb-0">
                                {meal.name}
                              </h1>
                            </Container>
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="instacart-container d-md-none">
                        {showInstacartButton && (
                          <div className="text-center">
                            <Button
                              color="instacart"
                              className="w-100 w-md-auto"
                              onClick={handleInstaCart}
                              disabled={preloadInstacart}
                            >
                              {preloadInstacart && (
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                />
                              )}
                              {t('mealDetails.instacart.button')}{' '}
                              <img
                                src={instacartLogo}
                                style={{
                                  verticalAlign: 'baseline',
                                  height: '13px',
                                }}
                                alt="instacart"
                              />
                            </Button>
                          </div>
                        )}
                      </div>

                      <div className="meal-info">
                        <div className="d-none d-md-block">
                          {renderTags()}
                          <h1 className="meal-title title-large semi-bold mb-xl">{meal.name}</h1>
                        </div>

                        <div className="grey-box mb-2">
                          {t('cookbook/prep-cook-time')}:{' '}
                          <strong className="float-end">
                            {calculatePreparationTime(meal.preparationTime.minutes)} min
                          </strong>
                        </div>

                        <div className="grey-box mb-3">
                          {t('cookbook/portions')}:{' '}
                          {meal.portionsAdjustable ? (
                            <strong className="float-end">
                              <button
                                className="servings-button me-3"
                                disabled={multiplier <= 1}
                                onClick={() => changeMultiplier(multiplier - 1)}
                              >
                                <img src={meal.portions === 1 ? minus : minusMulti} alt="" />
                              </button>
                              {meal.portions * multiplier}
                              <button
                                className="servings-button ms-3"
                                disabled={multiplier > 24}
                                onClick={() => changeMultiplier(multiplier + 1)}
                              >
                                <img src={meal.portions === 1 ? plus : plusMulti} alt="" />
                              </button>
                            </strong>
                          ) : (
                            <strong className="float-end">{meal.portions * multiplier}</strong>
                          )}
                        </div>

                        <div className="d-none d-md-block">
                          <CookbookNutritions meal={meal} multiplier={multiplier} />
                        </div>

                        {showInstacartButton && (
                          <div className="text-center d-none d-md-block">
                            <Button
                              color="instacart"
                              className="w-100 w-md-auto"
                              onClick={handleInstaCart}
                              disabled={preloadInstacart}
                            >
                              {preloadInstacart && (
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                />
                              )}
                              {t('mealDetails.instacart.button')}{' '}
                              <img
                                src={instacartLogo}
                                style={{
                                  verticalAlign: 'baseline',
                                  height: '13px',
                                }}
                                alt="instacart"
                              />
                            </Button>
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>

                  {meal.dishes.map((dish: any, idx: number) => (
                    <div key={dish.id}>
                      <CookbookDish {...dish}>
                        {renderIngredientList(dish.ingredients, dish.id)}
                      </CookbookDish>
                      {idx < meal.dishes.length - 1 && <hr key={idx} className="d-md-none" />}
                    </div>
                  ))}

                  {meal.recipeNote && (
                    <div className="smart-tip-container">
                      <div className="smart-tip">
                        <p className="semi-bold black">
                          <img src={star} alt="" className="me-2" />
                          {t('cookbook/tip')}
                        </p>
                        <p className="mb-0 small">{meal.recipeNote}</p>
                      </div>
                    </div>
                  )}

                  <div className="d-md-none">
                    <CookbookNutritions meal={meal} multiplier={multiplier} />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </Layout>
  );
};

const Placeholder = () => <UniversalPlaceholder titleBar="" />;

export default withLoading(withMobileAppMode(CookbookMealDetailsContainer), Placeholder);
